import { Component, HostListener, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { OrganizationConfig } from '../../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { getGiftProductTypeDisplay, GiftProductType } from '../../../../../../models/gift';
import {
  getGiftableProductKey,
  getGiftProductTargetForGiftableProduct,
  getGiftProductTargetKey,
  GIFTABLE_PRODUCT_GROUPS,
  GiftableProduct,
  GiftCenterCarouselImage,
  GiftCenterFeaturedGift,
  GiftCenterHomePreviewPayload,
  GiftProductPresentation,
  GiftProductPresentationWrite,
} from '../../../../../../models/gift-center';
import { GiftService } from '../../../../../../services/gift/gift.service';
import {
  SimpleMessageModalComponent
} from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import {
  ConfigureGiftCenterCarouselImageModalComponent
} from './modals/configure-gift-center-carousel-image-modal/configure-gift-center-carousel-image-modal.component';
import {
  GiftCenterHomePreviewModalComponent
} from './modals/gift-center-home-preview-modal/gift-center-home-preview-modal.component';
import { CallbackResult, CallbackResultStatus, isLightColor, StandardColors, StandardColorTeal } from '../../../../../../utils';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { MarkdownRenderService } from '../../../../../../services/markdown-render/markdown-render.service';

const MAX_CAROUSEL_IMAGES = 5;
const MAX_FEATURED_GIFTS = 6;
const SAVE_FAILED_MESSAGE = 'Saving failed. Please try again.';
const PREVIEW_FAILED_MESSAGE = 'The preview could not be created. Please try again.';
const CORRECT_ERRORS_MESSAGE = 'Please correct the errors above and try again.';

interface GiftTagRow {
  product: GiftableProduct;
  tag_text: string;
  tag_color_hex: string;
  short_description: string;
}


@Component({
  selector: 'app-gift-center-home-page',
  templateUrl: './gift-center-home-page.component.html',
  styleUrls: ['./gift-center-home-page.component.css']
})
export class GiftCenterHomePageComponent implements OnInit, ComponentHasUnsavedChanges {

  public orgConfig: OrganizationConfig = null;
  public giftableProducts: GiftableProduct[] = [];
  public carouselImages: GiftCenterCarouselImage[] = [];
  public featuredGifts: GiftCenterFeaturedGift[] = [];
  public giftTagRows: GiftTagRow[] = [];
  public newFeaturedGiftKey: string | null = null;

  public loadingOrgConfig = true;
  public loadingGiftableProducts = true;
  public loadingCarouselImages = true;
  public loadingFeaturedGifts = true;
  public loadingPresentations = true;
  public saving = false;
  public savingCarouselImages = false;
  public creatingPreview = false;

  public successMessage = '';
  public errorMessage = '';
  public errors: any = {};
  public modalResult: CallbackResult = null;

  public serverError: number = null;

  public readonly MAX_CAROUSEL_IMAGES = MAX_CAROUSEL_IMAGES;
  public readonly MAX_FEATURED_GIFTS = MAX_FEATURED_GIFTS;
  public readonly GIFTABLE_PRODUCT_GROUPS = GIFTABLE_PRODUCT_GROUPS;
  public readonly CallbackResultStatus = CallbackResultStatus;
  public readonly getGiftableProductKey = getGiftableProductKey;
  public readonly getGiftProductTypeDisplay = getGiftProductTypeDisplay;
  public readonly isLightColor = isLightColor;

  private presentations: GiftProductPresentation[] = [];
  private carouselImagesLoaded = false;
  private alertDirty = false;
  private carouselTitleDirty = false;
  private featuredGiftsDirty = false;
  private giftTagsDirty = false;
  private editCount = 0;

  constructor(
    private orgConfigService: OrganizationConfigService,
    private giftService: GiftService,
    private modalService: NgbModal,
    public markdownRenderService: MarkdownRenderService,
  ) {}

  ngOnInit() {
    this.loadOrganizationConfig();
    this.loadGiftableProductsAndPresentations();
    this.loadCarouselImages();
    this.loadFeaturedGifts();
  }

  public hasUnsavedChanges(): boolean {
    return this.alertDirty
      || this.carouselTitleDirty
      || this.featuredGiftsDirty
      || this.giftTagsDirty;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public markAlertDirty() {
    this.alertDirty = true;
    this.editCount++;
  }

  public markCarouselTitleDirty() {
    this.carouselTitleDirty = true;
    this.editCount++;
  }

  public markFeaturedGiftsDirty() {
    this.featuredGiftsDirty = true;
    this.editCount++;
  }

  public markGiftTagsDirty() {
    this.giftTagsDirty = true;
    this.editCount++;
  }

  /** Later reloads keep the page rendered and show the floating Loading alert. */
  public pageLoaded(): boolean {
    return this.orgConfig !== null
      && !this.loadingOrgConfig
      && !this.loadingGiftableProducts
      && !this.loadingPresentations
      && !this.loadingFeaturedGifts
      && this.carouselImagesLoaded;
  }

  public isLoading(): boolean {
    return this.loadingOrgConfig
      || this.loadingGiftableProducts
      || this.loadingCarouselImages
      || this.loadingFeaturedGifts
      || this.loadingPresentations;
  }

  public isSaving(): boolean {
    return this.saving || this.savingCarouselImages;
  }

  /** The alert color to show; blank means the first standard color. */
  public alertColorHex(): string {
    return this.orgConfig.gift_center_alert_color_hex || StandardColors[0].colorHex;
  }

  /** The color a tag gets when none is chosen: the organization's primary color, else teal. */
  public defaultTagColorHex(): string {
    const primary = this.orgConfig && this.orgConfig.custom_style_primary_color_hex;
    return primary && primary.length === 6 ? primary : StandardColorTeal.colorHex;
  }

  public alertMessageMissing(): boolean {
    return this.orgConfig.gift_center_alert_enabled
      && (this.orgConfig.gift_center_alert_text || '').trim() === '';
  }

  public alertDatesInverted(): boolean {
    const start = this.orgConfig.gift_center_alert_start_date;
    const end = this.orgConfig.gift_center_alert_end_date;
    return !!start && !!end && end < start;
  }

  public formInvalid(): boolean {
    return this.alertMessageMissing() || this.alertDatesInverted() || this.giftTagsInvalid();
  }

  public canSave(): boolean {
    return this.formReady()
      && this.hasUnsavedChanges()
      && !this.saving
      && !this.formInvalid();
  }

  public canPreview(): boolean {
    return this.formReady()
      && !this.creatingPreview
      && !this.formInvalid();
  }

  /** Saves each dirty section in turn, stopping at the first failure. */
  public save() {
    if (!this.canSave()) {
      return;
    }
    this.clearFeedback();
    this.saving = true;

    this.saveOrganizationConfig()
      .then(saved => saved && this.saveFeaturedGifts())
      .then(saved => saved && this.saveGiftTags())
      .then(saved => {
        if (saved) {
          this.successMessage = 'Gift Center home page settings saved.';
        }
      })
      .finally(() => { this.saving = false; });
  }

  public openPreview() {
    if (!this.canPreview()) {
      return;
    }
    this.clearFeedback();
    this.creatingPreview = true;

    this.giftService
      .createGiftCenterHomePreview(this.buildPreviewPayload())
      .then(preview => {
        const modalRef = this.modalService.open(GiftCenterHomePreviewModalComponent, {
          size: 'xl',
          scrollable: true,
          windowClass: 'gift-center-preview-modal',
        });
        modalRef.componentInstance.previewUrl = preview.preview_url;
        modalRef.componentInstance.givePreviewUrl = preview.give_preview_url;
      })
      .catch(reason => this.showRequestError(reason, CORRECT_ERRORS_MESSAGE, PREVIEW_FAILED_MESSAGE))
      .finally(() => { this.creatingPreview = false; });
  }

  public carouselImageSummary(image: GiftCenterCarouselImage): string {
    if (!image.product_type) {
      return 'No button';
    }
    return '"' + image.button_text + '" links to ' + image.product_name;
  }

  public handleAddCarouselImage() {
    this.openCarouselImageModal(null);
  }

  public handleEditCarouselImage(image: GiftCenterCarouselImage) {
    this.openCarouselImageModal(image);
  }

  public handleRemoveCarouselImage(image: GiftCenterCarouselImage) {
    this.clearFeedback();

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Remove Image';
    modalRef.componentInstance.messageText = 'Are you sure you want to remove this image from the carousel?';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.savingCarouselImages = true;

      this.giftService
        .deleteGiftCenterCarouselImage(image.url)
        .then(() => {
          this.successMessage = 'Image removed successfully.';
          this.loadCarouselImages();
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to remove this image.';
          } else {
            this.errorMessage = SAVE_FAILED_MESSAGE;
          }
        })
        .finally(() => { this.savingCarouselImages = false; });
    };
  }

  public dropCarouselImage(event: CdkDragDrop<GiftCenterCarouselImage[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    this.clearFeedback();
    moveItemInArray(this.carouselImages, event.previousIndex, event.currentIndex);
    this.savingCarouselImages = true;

    this.giftService
      .setGiftCenterCarouselImageOrder(this.carouselImages.map(image => image.id))
      .then(images => {
        this.carouselImages = images;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error.useful_error_message || 'The new image order could not be saved.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to reorder these images.';
        } else {
          this.errorMessage = SAVE_FAILED_MESSAGE;
        }
        this.loadCarouselImages();
      })
      .finally(() => { this.savingCarouselImages = false; });
  }

  public productsOfType(products: GiftableProduct[], productType: GiftProductType): GiftableProduct[] {
    return products.filter(p => p.product_type === productType);
  }

  public unfeaturedGiftableProducts(): GiftableProduct[] {
    const featuredKeys = this.featuredGifts.map(gift => getGiftProductTargetKey(gift));
    return this.giftableProducts.filter(p => !featuredKeys.includes(getGiftableProductKey(p)));
  }

  public addFeaturedGift() {
    const product = this.giftableProducts.find(p => getGiftableProductKey(p) === this.newFeaturedGiftKey);
    if (!product || this.featuredGifts.length >= MAX_FEATURED_GIFTS) {
      return;
    }
    const tagRow = this.giftTagRows.find(row => row.product === product);
    const tagText = tagRow ? tagRow.tag_text.trim() : '';
    this.featuredGifts.push({
      id: null,
      order_number: this.featuredGifts.length,
      ...getGiftProductTargetForGiftableProduct(product),
      product_name: product.name,
      price_display: product.price_display,
      is_giftable: true,
      tag_text: tagText,
      tag_color_hex: tagText ? (tagRow.tag_color_hex || this.defaultTagColorHex()) : '',
      short_description: tagRow ? tagRow.short_description.trim() : '',
    });
    this.newFeaturedGiftKey = null;
    this.markFeaturedGiftsDirty();
  }

  public removeFeaturedGift(index: number) {
    this.featuredGifts.splice(index, 1);
    this.markFeaturedGiftsDirty();
  }

  public dropFeaturedGift(event: CdkDragDrop<GiftCenterFeaturedGift[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.featuredGifts, event.previousIndex, event.currentIndex);
    this.markFeaturedGiftsDirty();
  }

  public giftTagRowError(row: GiftTagRow): string {
    const hasText = row.tag_text.trim() !== '';
    const hasColor = row.tag_color_hex !== '';
    if (hasColor && !hasText) {
      return 'Enter text for this tag, or remove its color.';
    }
    return '';
  }

  public giftTagsInvalid(): boolean {
    return this.giftTagRows.some(row => this.giftTagRowError(row) !== '');
  }

  private formReady(): boolean {
    return this.serverError === null && this.pageLoaded();
  }

  private saveOrganizationConfig(): Promise<boolean> {
    if (!this.alertDirty && !this.carouselTitleDirty) {
      return Promise.resolve(true);
    }
    const editsAtRequest = this.editCount;
    return this.orgConfigService
      .updateGiftCenterHomePageSettings(this.orgConfig)
      .then(updatedOrgConfig => {
        if (this.editCount !== editsAtRequest) {
          return true;
        }
        this.orgConfig.gift_center_alert_enabled = updatedOrgConfig.gift_center_alert_enabled;
        this.orgConfig.gift_center_alert_text = updatedOrgConfig.gift_center_alert_text;
        this.orgConfig.gift_center_alert_color_hex = updatedOrgConfig.gift_center_alert_color_hex;
        this.orgConfig.gift_center_alert_start_date = updatedOrgConfig.gift_center_alert_start_date;
        this.orgConfig.gift_center_alert_end_date = updatedOrgConfig.gift_center_alert_end_date;
        this.orgConfig.gift_center_carousel_title = updatedOrgConfig.gift_center_carousel_title;
        this.alertDirty = false;
        this.carouselTitleDirty = false;
        return true;
      })
      .catch(reason => {
        this.showRequestError(reason, CORRECT_ERRORS_MESSAGE, SAVE_FAILED_MESSAGE);
        return false;
      });
  }

  private saveFeaturedGifts(): Promise<boolean> {
    if (!this.featuredGiftsDirty) {
      return Promise.resolve(true);
    }
    const editsAtRequest = this.editCount;
    return this.giftService
      .setGiftCenterFeaturedGifts(this.featuredGifts)
      .then(featuredGifts => {
        if (this.editCount !== editsAtRequest) {
          return true;
        }
        this.featuredGifts = featuredGifts;
        this.featuredGiftsDirty = false;
        return true;
      })
      .catch(reason => {
        this.showRequestError(reason, 'Popular gifts could not be saved.', SAVE_FAILED_MESSAGE);
        return false;
      });
  }

  private saveGiftTags(): Promise<boolean> {
    if (!this.giftTagsDirty) {
      return Promise.resolve(true);
    }
    const editsAtRequest = this.editCount;
    return this.giftService
      .setGiftProductPresentations(this.buildPresentationRows())
      .then(presentations => {
        if (this.editCount !== editsAtRequest) {
          return true;
        }
        this.presentations = presentations;
        this.applyPresentationTags();
        this.giftTagsDirty = false;
        return true;
      })
      .catch(reason => {
        this.showRequestError(reason, 'Gift tags and descriptions could not be saved.', SAVE_FAILED_MESSAGE);
        return false;
      });
  }

  private buildPreviewPayload(): GiftCenterHomePreviewPayload {
    return {
      gift_center_alert_enabled: this.orgConfig.gift_center_alert_enabled,
      gift_center_alert_text: this.orgConfig.gift_center_alert_text,
      gift_center_alert_color_hex: this.orgConfig.gift_center_alert_color_hex || '',
      gift_center_alert_start_date: this.orgConfig.gift_center_alert_start_date || null,
      gift_center_alert_end_date: this.orgConfig.gift_center_alert_end_date || null,
      gift_center_carousel_title: this.orgConfig.gift_center_carousel_title,
      featured_gifts: this.featuredGifts.map(gift => ({
        product_type: gift.product_type,
        membership_id: gift.membership_id,
        entry_pass_id: gift.entry_pass_id,
        voucher_pack_id: gift.voucher_pack_id,
      })),
      presentations: this.buildPresentationRows(),
    };
  }

  private buildPresentationRows(): GiftProductPresentationWrite[] {
    return this.giftTagRows.map(row => {
      const tagText = row.tag_text.trim();
      return {
        ...getGiftProductTargetForGiftableProduct(row.product),
        tag_text: tagText,
        tag_color_hex: tagText ? (row.tag_color_hex || this.defaultTagColorHex()) : '',
        short_description: row.short_description.trim(),
      };
    });
  }

  private showRequestError(reason: any, invalidMessage: string, failedMessage: string) {
    if (reason.status === 400) {
      const error = reason.error || {};
      this.errors = error;
      this.errorMessage = error.useful_error_message
        || (error.non_field_errors && error.non_field_errors[0])
        || invalidMessage;
    } else if (reason.status === 403) {
      this.errorMessage = 'You do not have permission to configure the Gift Center.';
    } else {
      this.errorMessage = failedMessage;
    }
  }

  private openCarouselImageModal(image: GiftCenterCarouselImage | null) {
    this.clearFeedback();

    const modalRef = this.modalService.open(ConfigureGiftCenterCarouselImageModalComponent);
    modalRef.componentInstance.image = image;
    modalRef.componentInstance.giftableProducts = this.giftableProducts;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadCarouselImages();
    };
  }

  private clearFeedback() {
    this.successMessage = '';
    this.errorMessage = '';
    this.errors = {};
    this.modalResult = null;
  }

  /**
   * Fill the tag table from the saved presentations, and refresh the tags shown
   * on the products and the popular gifts.
   */
  private applyPresentationTags() {
    const presentationsByKey = new Map<string, GiftProductPresentation>();
    for (const presentation of this.presentations) {
      presentationsByKey.set(getGiftProductTargetKey(presentation), presentation);
    }

    this.giftTagRows = this.giftableProducts.map(product => {
      const presentation = presentationsByKey.get(getGiftableProductKey(product));
      product.tag_text = presentation ? presentation.tag_text : '';
      product.tag_color_hex = presentation ? presentation.tag_color_hex : '';
      product.short_description = presentation ? presentation.short_description : '';
      return {
        product,
        tag_text: product.tag_text,
        tag_color_hex: product.tag_color_hex,
        short_description: product.short_description,
      };
    });

    for (const gift of this.featuredGifts) {
      const presentation = presentationsByKey.get(getGiftProductTargetKey(gift));
      gift.tag_text = presentation ? presentation.tag_text : '';
      gift.tag_color_hex = presentation ? presentation.tag_color_hex : '';
      gift.short_description = presentation ? presentation.short_description : '';
    }
  }

  private loadOrganizationConfig() {
    this.loadingOrgConfig = true;

    this.orgConfigService
      .loadOrganizationConfig()
      .then(() => {
        const loadError = this.orgConfigService.getOrganizationConfigServerError();
        if (loadError !== null) {
          this.serverError = loadError;
        } else {
          this.orgConfig = this.orgConfigService.getOrganizationConfig();
        }
        this.loadingOrgConfig = false;
      });
  }

  private loadGiftableProductsAndPresentations() {
    this.loadingGiftableProducts = true;
    this.loadingPresentations = true;

    Promise.all([
      this.giftService.getGiftCenterGiftableProducts(),
      this.giftService.getGiftProductPresentations(),
    ])
      .then(([products, presentations]) => {
        this.giftableProducts = products;
        this.presentations = presentations;
        this.applyPresentationTags();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        this.loadingGiftableProducts = false;
        this.loadingPresentations = false;
      });
  }

  private loadCarouselImages() {
    this.loadingCarouselImages = true;

    this.giftService
      .getGiftCenterCarouselImages()
      .then(images => {
        this.carouselImages = images;
        this.carouselImagesLoaded = true;
      })
      .catch(reason => {
        if (this.carouselImagesLoaded) {
          this.errorMessage = 'The images could not be reloaded. Please refresh the page.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCarouselImages = false; });
  }

  private loadFeaturedGifts() {
    this.loadingFeaturedGifts = true;

    this.giftService
      .getGiftCenterFeaturedGifts()
      .then(featuredGifts => {
        this.featuredGifts = featuredGifts;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingFeaturedGifts = false; });
  }
}
